// Purchase / จัดซื้อ module — Xano contract + helpers for the 5-stage procurement
// flow. Companion to lib/stock.js (a completed purchase writes รับเพิ่ม rows into
// the stock ledger) and lib/expense.js (confirming payment creates an expense).
//
// Tables (see the Xano SSDC workspace):
// ── TWO branches, two meanings (do not conflate) ────────────────────────────
//   inventory_request.branch_id — the branch that NEEDS the item and raised the
//     request. Stays per line, even after lines are merged under one PO.
//   procurement.branch_id       — the branch that carries the PAPERWORK and the
//     EXPENSE for the whole PO. Chosen when the PO leaves ตรวจสอบ (sent as
//     `procurement_branch_id`); Xano copies it onto the expense it creates at
//     ชำระเงิน. It is NOT stored anywhere while the PO is still in ตรวจสอบ.
//   So one PO may legitimately span several requesting branches while billing to
//   a single one — merging lines must never rewrite their branch_id.
//
//   inventory_request { id, date, inventory_id, branch_id, N, price, approve,
//                       approve_date, stage(1-5), PO_number, seller_id, remark,
//                       created_by, modified_by, Creation_Date, Modified_Date,
//                       active, transfer, branch2_id }
//     — ONE row per item line. price = the LINE TOTAL (its unit cost = price / N).
//   procurement { id, PO_number(UNIQUE), seller_id, price, expense_id,
//                 inventory_request_id[](the grouped lines), file[], note, date,
//                 branch_id, active, created_by/at, modified_by/at }
//     — the PO-level record, created when a group advances Inspection → Payment.
//
// ── The five stages (inventory_request.stage) ───────────────────────────────
//   1 คำขอซื้อ   Request    — any employee raises a line. Managers approve → 2.
//   2 ตรวจสอบ    Inspection — manager groups lines under a PO (merge/unmerge),
//                             assigns seller (optional) + branch, then → 3.
//   3 ชำระเงิน   Payment    — manager attaches quotations, enters per-line price
//                             + VAT/discount, commits a seller, confirms → 4,
//                             which auto-creates the expense.
//   4 รับของ     Receive    — any employee records received qty per line (partial
//                             allowed); each writes a รับเพิ่ม stock row. When every
//                             line is fully received the PO moves → 5.
//   5 บันทึก     Log        — read-only history.
//
// Actions live on the tab whose stage they advance FROM. The `step` you send to
// /api/purchase/step is the stage advancing FROM (Xano stores ±1 per `type`).

import { todayISO } from './stock.js'

export const STAGE_REQUEST = 1
export const STAGE_INSPECT = 2
export const STAGE_PAYMENT = 3
export const STAGE_RECEIVE = 4
export const STAGE_LOG     = 5

export const STAGES = [
  { stage: STAGE_REQUEST, key: 'request', label: 'คำขอซื้อ', desc: 'คำขอซื้อที่รอผู้จัดการอนุมัติ', managerOnly: false },
  { stage: STAGE_INSPECT, key: 'inspect', label: 'ตรวจสอบ',  desc: 'อนุมัติแล้ว — จัดกลุ่ม PO · เลือกสาขาที่รับผิดชอบ ก่อนส่งไปชำระเงิน', managerOnly: true },
  { stage: STAGE_PAYMENT, key: 'payment', label: 'ชำระเงิน', desc: 'ระบุราคาต่อรายการ · เลือกผู้ขาย แล้วยืนยันการชำระเงิน (ย้อนกลับไปตรวจสอบได้)', managerOnly: true },
  { stage: STAGE_RECEIVE, key: 'receive', label: 'รับของ',   desc: 'รับของทีละรายการ (รับบางส่วนได้) · ปิด PO แล้วระบบจะบันทึกส่วนที่เหลือเข้า ตัด Stock ให้', managerOnly: false },
  { stage: STAGE_LOG,     key: 'log',     label: 'บันทึก',   desc: 'ประวัติการจัดซื้อที่เสร็จสิ้น (อ่านอย่างเดียว)', managerOnly: false },
]

export const stageMeta = (stage) => STAGES.find((s) => s.stage === Number(stage)) || null

// Pill tone per stage: request = amber (needs a decision), inspect = indigo,
// payment = violet, receive = sky (in transit), log = emerald (settled).
export function stageTone(stage) {
  switch (Number(stage)) {
    case STAGE_REQUEST: return 'bg-amber-50 text-amber-800 border-amber-200'
    case STAGE_INSPECT: return 'bg-indigo-50 text-indigo-700 border-indigo-200'
    case STAGE_PAYMENT: return 'bg-violet-50 text-violet-700 border-violet-200'
    case STAGE_RECEIVE: return 'bg-sky-50 text-sky-700 border-sky-200'
    case STAGE_LOG:     return 'bg-emerald-50 text-emerald-700 border-emerald-200'
    default:            return 'bg-slate-50 text-slate-600 border-slate-200'
  }
}

/* ── read model (GET /api/purchase row = an inventory_request row) ────────────
 * Enriched server-side (see Xano GET /inventory_request). Read these; never
 * recompute them:
 *   Creator / Modifier          — employee `code` strings (eval, not objects)
 *   branch / branch_color       — origin branch name + colour
 *   inventory{}                 — the full item (Name, unit, subCategory, image…)
 *   seller{ code, name, bank… } — the assigned vendor (present once chosen)
 *   stock_count (merged)        — current level of this item at the branch
 */
export const reqItemName = (r) => String(r?.inventory?.Name || '').trim() || `#${r?.inventory_id ?? '—'}`
export const reqUnit     = (r) => String(r?.inventory?.unit || '').trim()
export const reqQty      = (r) => Math.trunc(Number(r?.N) || 0)
// How much of this line has actually arrived — the sum of its รับเพิ่ม stock rows,
// served by the `stock_received` addon on GET /inventory_request. A line can have
// several receipts (partial deliveries) and may exceed what was ordered.
export const reqReceived    = (r) => Math.trunc(Number(r?.received) || 0)
export const reqOutstanding = (r) => Math.max(0, reqQty(r) - reqReceived(r))
export const lineReceived   = (r) => reqReceived(r) >= reqQty(r)
export const lineOverReceived = (r) => reqReceived(r) > reqQty(r)
export const poNumber    = (r) => String(r?.PO_number || '').trim()
export const creatorCode = (r) => r?.Creator || r?._creator?.code || ''
export const modifierCode = (r) => r?.Modifier || r?._modifier?.code || ''
export const branchName  = (r) => r?.branch || r?.branch_name || (r?.branch_id ? `#${r.branch_id}` : '—')
export const sellerName  = (r) => r?.seller?.name || r?.seller_name || ''
export const sellerCode  = (r) => r?.seller?.code || ''
// The PO-level record rides along on every line via the `procurement_of` addon,
// so the list knows what the PO itself holds without a fetch per card: whether an
// expense has been booked (expense_id) and which branch is paying for it.
export const procOf      = (r) => r?.procurement || null
export const procExpenseId = (r) => Math.trunc(Number(r?.procurement?.expense_id) || 0)

// price = the LINE TOTAL for that item line (its unit cost is price / N). May be
// null until captured at Payment. Returns null when unset so the UI shows "—"
// rather than a confident 0.
export function lineGoods(r) {
  const v = r?.price
  if (v == null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}
export function lineUnitGoods(r) {
  const total = lineGoods(r)
  const n = reqQty(r)
  if (total == null || n <= 0) return null
  return total / n
}

/**
 * Group flat request rows by PO_number. A row with no PO (a stage-1 line not yet
 * approved) forms its own singleton group keyed `#<id>` so nothing is dropped.
 * Returns `[{ po, isReal, rows, qty, lineCount, branchId, branchName, sellerId,
 * sellerName, goodsTotal }]`, ordered by PO desc then first row's date desc.
 */
export function groupByPO(rows) {
  const list = Array.isArray(rows) ? rows : []
  const map = new Map()
  for (const r of list) {
    const po = poNumber(r)
    const key = po || `#${r.id}`
    if (!map.has(key)) map.set(key, { po, isReal: !!po, rows: [] })
    map.get(key).rows.push(r)
  }
  const groups = [...map.values()].map((g) => {
    const first = g.rows[0] || {}
    const goodsVals = g.rows.map(lineGoods).filter((v) => v != null)
    return {
      ...g,
      key: g.po || `#${first.id}`,
      qty: g.rows.reduce((s, r) => s + reqQty(r), 0),
      lineCount: g.rows.length,
      branchId: first.branch_id,
      branchName: branchName(first),
      branchIds: [...new Set(g.rows.map((r) => Number(r.branch_id) || 0))],
      sellerId: Number(first.seller_id) || 0,
      sellerName: sellerName(first),
      sellerCode: sellerCode(first),
      expenseId: procExpenseId(first),
      // procurement.branch_id is the stored paying branch. Falls back to the first
      // line's branch only while the PO has no record yet (before ตรวจสอบ sends it on).
      procBranchId: Number(first?.procurement?.branch_id) || 0,
      goodsTotal: goodsVals.length ? goodsVals.reduce((s, v) => s + v, 0) : null,
      date: first.date,
      // Receiving progress across the PO. `allReceived` is what closes it: the
      // whole PO waits in รับของ until every line is in.
      receivedLines: g.rows.filter(lineReceived).length,
      outstandingQty: g.rows.reduce((s, r) => s + reqOutstanding(r), 0),
      allReceived: g.rows.every(lineReceived),
    }
  })
  for (const g of groups) g.multiBranch = g.branchIds.length > 1
  groups.sort((a, b) => {
    const p = String(b.po || '').localeCompare(String(a.po || ''))
    if (p !== 0) return p
    return String(b.date || '').localeCompare(String(a.date || ''))
  })
  return groups
}

/* ── landed-cost allocation (Payment stage) ──────────────────────────────────
 * Per-item cost is captured PER LINE, never derived from a PO total. At payment
 * confirm the after-VAT cost `pool` is spread across the lines pro-rata by their
 * goods value, so each unit cost reflects the true all-in (landed) cost and the
 * lines always sum back to the pool.
 *
 *   weight_i     = goods_i / Σ goods
 *   landed_i     = weight_i × pool
 *   unit_cost_i  = landed_i / N_i
 *
 * `lines` = [{ id, price (goods line total), N }]. `pool` = the after-VAT total
 * (goods − discount + VAT) from lib/expense.js#computeAmount. Falls back to a
 * goods-only split when no pool is supplied.
 */
export function allocateLanded(lines, pool) {
  const arr = Array.isArray(lines) ? lines : []
  const goods = arr.map((l) => Math.max(0, Number(l.price) || 0))
  const sumGoods = goods.reduce((s, v) => s + v, 0)
  const total = pool != null && Number.isFinite(Number(pool)) ? Number(pool) : sumGoods
  return arr.map((l, i) => {
    const n = Math.trunc(Number(l.N) || 0)
    const landed = sumGoods > 0 ? (goods[i] / sumGoods) * total : 0
    return { ...l, goods: goods[i], landed, unitCost: n > 0 ? landed / n : 0 }
  })
}

/* ── payload builders ────────────────────────────────────────────────────── */

// A new stage-1 request line (one item). The proxy stamps created_by/modified_by
// and forces stage:1; Creation_Date + PO_number default server-side. `price` is
// an OPTIONAL requester estimate (line total) — usually left blank until Payment.
//
// `date` = the day the request was raised. Xano leaves it null when unsent (its
// upsert has no default for it) and the list then shows "—", so default it to
// today here — every caller wants that, and a request with no date can't be
// sorted or reported on.
export function buildRequestPayload({ inventory_id, branch_id, qty, remark, price, date }) {
  const body = {
    inventory_request_id: 0,               // 0 = insert
    inventory_id: Number(inventory_id) || 0,
    branch_id: Number(branch_id) || 0,
    N: Math.trunc(Number(qty) || 0),
    remark: remark || '',
    stage: STAGE_REQUEST,
    date: date || todayISO(),
  }
  if (price != null && price !== '') body.price = Number(price)
  return body
}

// A NEW line added straight into an existing stage-2 PO (from the PO editor).
// Passing stage 2 + a non-empty PO_number skips Xano's "mint a fresh PO" branch,
// so the line lands under the PO it was added to; `approve` matches the lines it
// joins (they cleared stage 1 already) — leaving it blank would show the line as
// un-approved in the ตรวจสอบ card.
export function buildPoLinePayload({ inventory_id, branch_id, qty, remark, po, date, stage = STAGE_INSPECT }) {
  return {
    inventory_request_id: 0,
    inventory_id: Number(inventory_id) || 0,
    branch_id: Number(branch_id) || 0,
    N: Math.trunc(Number(qty) || 0),
    remark: remark || '',
    stage,
    PO_number: String(po || ''),
    approve: 'อนุมัติ',
    date: date || todayISO(),
  }
}

// An EDIT of an existing line. Xano's POST /inventory_request is a full upsert
// built on a `dblink` input: `inventory_id`, `branch_id` and `stage` are REQUIRED
// inputs (a partial body 400s with `Missing param: inventory_id`), and the ones
// that do have an `?? existing` fallback still fall back only on null — an
// omitted text input arrives as "" and would blank the stored value (PO_number
// especially, which then triggers a fresh PO number). So every edit must be a
// read-modify-write: echo the row back, then apply the patch.
const EDIT_FIELDS = [
  'date', 'inventory_id', 'branch_id', 'approve', 'approve_date', 'stage',
  'N', 'remark', 'created_by', 'seller_id', 'PO_number', 'price',
]
export function buildEditPayload(row, patch = {}) {
  const body = { inventory_request_id: Number(row?.id) || 0 }
  for (const k of EDIT_FIELDS) {
    if (row?.[k] !== undefined) body[k] = row[k]
  }
  return { ...body, ...patch }
}

/* ── procurement (the PO-level record) ───────────────────────────────────────
 * One row per PO_number, carrying what belongs to the PO rather than to a line:
 * seller, the branch that pays, a note, and the attachments. It is created on
 * demand by POST /api/procurement — a PO in ตรวจสอบ usually has no row yet, so a
 * 404 from the GET means "nothing saved", not an error.
 */
export async function fetchProcurement(get, po) {
  if (!po) return null
  try {
    return await get('/api/procurement', { PO_number: po })
  } catch (e) {
    if (e?.status === 404) return null
    throw e
  }
}

// Xano's file[] entry shape. `location` is the Wasabi key — everything else is
// display metadata the old system also writes, kept so both systems can read
// each other's attachments.
export function toProcurementFile(uploaded) {
  return {
    location: uploaded?.location || '',
    name: uploaded?.name || '',
    url: uploaded?.url || '',
    thumbnail: '',
    type: uploaded?.type || '',
    size: Number(uploaded?.size) || 0,
    tag: [],
    note: '',
    presigned_expired: uploaded?.presigned_expired ?? null,
    created_at: uploaded?.created_at ?? null,
    modified_at: uploaded?.modified_at ?? null,
    user_id: 0,
    uuid: uploaded?.uuid || '',
  }
}

export const fileUrl = (f) =>
  f?.location ? `/api/purchase-file/${String(f.location).split('/').map(encodeURIComponent).join('/')}` : ''

/* ── formatting ──────────────────────────────────────────────────────────── */

export function fmtBaht(v) {
  if (v == null || v === '' || !Number.isFinite(Number(v))) return '—'
  return Number(v).toLocaleString('th-TH', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}

export function fmtDate(iso) {
  if (!iso) return '—'
  const [y, m, d] = String(iso).split('-')
  if (!y) return iso
  return `${Number(d)}/${Number(m)}/${String(y).slice(2)}`
}
